import React from 'react';
import { Rocket, FlaskConical, AlertTriangle, ExternalLink, ShieldCheck, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function LaunchpadLanding() {
  const launches = [
    {
      id: 'alien',
      name: 'Alien Rizz',
      ticker: 'ALRIZZ',
      status: 'Live',
      marketCap: '$42,069',
      holders: 1337,
      image: '/launchpad/assets/alien/logo.png',
      description: 'The first coin from the BrainRotBlast Labs. Transparent tokenomics for maximum aura.'
    }
  ];

  return (
    <div className="space-y-12 animate-in slide-in-from-bottom-8 duration-700">
      {/* Short Explanation Block */}
      <div className="bg-emerald-500/5 border-4 border-emerald-500/10 rounded-[3rem] p-8 md:p-12 space-y-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 text-emerald-500/10 pointer-events-none opacity-20"><FlaskConical size={160} /></div>
        <div className="max-w-3xl space-y-4">
          <h2 className="text-4xl md:text-5xl font-bungee text-emerald-400 uppercase tracking-tighter leading-none">THE LABS PROGRAM</h2>
          <p className="text-sm md:text-base font-bungee text-emerald-100/80 leading-relaxed">
            BrainRotBlast is an AI meme image generator. 
            <span className="text-emerald-400"> Launchpad</span> is a separate Labs project where we turn approved memes into Solana tokens with published tokenomics + sell caps.
          </p>
          <p className="text-xs font-bungee text-emerald-500/50 uppercase tracking-widest">
            You can ignore this and still use BrainRotBlast normally.
          </p>
        </div>
      </div>

      {/* Launches Grid */}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-l-4 border-emerald-500 pl-4 py-1">
          <span className="text-[10px] font-black text-emerald-500/50 uppercase tracking-widest">ACTIVE EXPERIMENTS:</span>
          <span className="text-sm font-bungee text-white uppercase tracking-tight">LIVE LAUNCHES</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {launches.map((launch) => (
            <Link 
              key={launch.id} 
              to={`/launchpad/${launch.id}`}
              className="group bg-zinc-900/80 border-2 border-emerald-500/10 rounded-[2.5rem] overflow-hidden hover:border-emerald-500/40 transition-all hover:scale-[1.02] active:scale-95 shadow-2xl"
            >
              <div className="aspect-square bg-zinc-800 relative overflow-hidden">
                <img src={launch.image} alt={launch.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute top-4 right-4 bg-emerald-500 text-black px-4 py-1.5 rounded-full font-bungee text-[10px] uppercase shadow-lg z-10 animate-pulse">
                  {launch.status}
                </div>
                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black via-black/80 to-transparent">
                  <h3 className="text-3xl font-bungee text-white uppercase tracking-tight">{launch.name}</h3>
                  <span className="text-emerald-400 font-bungee text-sm">${launch.ticker}</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-xs text-zinc-500 font-bungee leading-relaxed line-clamp-2">
                  {launch.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-emerald-500/10">
                  <div className="space-y-1">
                    <span className="text-[8px] font-black text-zinc-600 uppercase tracking-widest">Market Cap</span>
                    <p className="text-sm font-bungee text-emerald-400">{launch.marketCap}</p>
                  </div>
                  <div className="space-y-1 text-right">
                    <span className="text-[8px] font-black text-zinc-600 uppercase tracking-widest">Holders</span>
                    <p className="text-sm font-bungee text-white">{launch.holders.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-zinc-900/50 border-2 border-emerald-500/5 rounded-[3rem] p-8 md:p-12 space-y-10">
        <div className="text-center space-y-2">
          <h3 className="text-3xl font-bungee text-emerald-400 uppercase tracking-tight">FAQ (LABS)</h3>
          <p className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Transparency First</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-3 p-6 bg-black/40 rounded-2xl border border-emerald-500/10">
            <h4 className="font-bungee text-sm text-emerald-400 uppercase">What is Launchpad?</h4>
            <p className="text-xs text-zinc-500 font-bungee leading-relaxed">
              Launchpad is our experimental program for transparent meme-coin launches. We take the most viral memes from BrainRotBlast and turn them into Solana tokens with public tokenomics.
            </p>
          </div>
          <div className="space-y-3 p-6 bg-black/40 rounded-2xl border border-emerald-500/10">
            <h4 className="font-bungee text-sm text-emerald-400 uppercase">Is this required to use BrainRotBlast?</h4>
            <p className="text-xs text-zinc-500 font-bungee leading-relaxed">
              No. BrainRotBlast is and will always be an AI image generator. Launchpad is a separate module you can completely ignore if you aren't interested in crypto experiments.
            </p>
          </div>
          <div className="space-y-3 p-6 bg-black/40 rounded-2xl border border-emerald-500/10">
            <h4 className="font-bungee text-sm text-emerald-400 uppercase">Are these investments?</h4>
            <p className="text-xs text-zinc-500 font-bungee leading-relaxed">
              No. These are purely experimental tokens for entertainment and research. There are no promises of value, profit, or utility. Do not put in more than you are willing to lose.
            </p>
          </div>
          <div className="space-y-3 p-6 bg-black/40 rounded-2xl border border-emerald-500/10">
            <h4 className="font-bungee text-sm text-emerald-400 uppercase">How do you avoid pump-and-dumps?</h4>
            <p className="text-xs text-zinc-500 font-bungee leading-relaxed">
              We use strict tokenomics, public founder wallets, published sell policies, and authority revocation on all contracts to ensure maximum transparency and fairness.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
